// cspell:disable

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import blogFetch from '../../axios/config';
import toast from 'react-hot-toast';
import { ModalApontamentoPmontProps } from './Types';

const ModalApontamentoPmont: React.FC<ModalApontamentoPmontProps> = ({ show, onHide }) => {

    const [peca, setPeca] = useState({
        ordem: "",
        tipoPeca: "",
        codigoVeiculo: "",
        projeto: "",
        veiculo: "",
        ultimoApont: ""
    });

    const inputRef = useRef<HTMLInputElement>(null);
    const apontandoRef = useRef<boolean>(false);
    const [matricula, setMatricula] = useState<string>("");
    const [matriculaSAP, setMatriculaSAP] = useState<string>("");
    const [nomeUsuario, setNomeUsuario] = useState<string>("");
    const [validandoMatricula, setValidandoMatricula] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);


    const validarMatriculaSAP = useCallback(async (matricula: string): Promise<{ nome: string; matriculaSAP: string } | null> => {
        const valor = matricula.trim();
        if (!/^\d{6}$/.test(valor)) return null;
        try {
            const { data } = await blogFetch.get(
                `IAcompanhamentoMontagem/validate-matricula/${encodeURIComponent(valor)}`
            );
            if (!data?.exists) return null;
            return {
                nome: data.nome ?? "",
                matriculaSAP: data.matricula_SAP ?? "",
            };
        } catch (err) {
            // 404 (exists:false) cai aqui via axios; tratamos como inválida
            return null;
        }
    }, []);

    const dispararApontamento = async (ordemRaw: string, codigoVeiculoAtual: string) => {
        if (apontandoRef.current) return;

        const ordem = ordemRaw.trim();
        if (!ordem) {
            toast.error("Por favor, insira a ordem da peça.");
            return;
        }
        if (!/^\d{6}$/.test(matricula) || !matriculaSAP) {
            toast.error("Registre uma matrícula SAP válida.");
            return;
        }

        apontandoRef.current = true;
        setIsSubmitting(true);
        try {
            // Se a peça ainda não foi carregada (leitor rápido antes do debounce), busca agora.
            let codigoVeiculo = codigoVeiculoAtual;
            if (!codigoVeiculo.trim()) {
                const resultado = await buscarPeca(ordem);
                if (!resultado) return;
                codigoVeiculo = resultado.codigoVeiculo;
            }
            if (!codigoVeiculo.trim()) {
                toast.error("Informe uma ordem válida com os dados da peça para apontar.");
                return;
            }

            const payload = {
                NumeroOrdem: ordem,
                CodigoPeca: codigoVeiculo,
                Quantidade: 1,
                DataApontamento: new Date(Date.now() - 3 * 3600 * 1000).toISOString(),
                MatriculaSap: matriculaSAP,
            };
            resetForm();
            inputRef.current?.focus();
        } catch (error: any) {
            console.error("Erro ao realizar apontamento:", error);
            const apiData = error.response?.data;
            const mensagemErro = typeof apiData === "string"
                ? apiData
                : (apiData?.mensagem || apiData?.message);
            toast.error(mensagemErro || "Erro ao realizar apontamento.");
        } finally {
            setIsSubmitting(false);
            apontandoRef.current = false;
        }
    };

    const handleSubmit = () => dispararApontamento(peca.ordem, peca.codigoVeiculo);

    const handleOrdemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        dispararApontamento(peca.ordem, peca.codigoVeiculo);
    };

    const resetForm = () => {
        setPeca({
            ordem: "",
            tipoPeca: "",
            codigoVeiculo: "",
            projeto: "",
            veiculo: "",
            ultimoApont: ""
        });
    };

    const handleCancel = () => {
        resetForm();
        onHide();
    };

    // Aceita apenas números, máximo 6 dígitos; limpa a validação anterior ao editar
    const handleMatriculaChange = (valor: string) => {
        setMatricula(valor.replace(/\D/g, "").slice(0, 6));
        setMatriculaSAP("");
        setNomeUsuario("");
    };

    // Ao abrir o modal, prefill da matrícula salva (compartilhada com o 14MONTAG) e re-valida
    useEffect(() => {
        if (!show) return;
        const matriculaStorage = localStorage.getItem("listaMontagem_matriculas_SAP");
        if (!matriculaStorage) return;
        let valor = "";
        let nomeSalvo = "";
        let matriculaSAPSalva = "";
        try {
            const obj = JSON.parse(matriculaStorage);
            valor = obj.principal || obj.secundaria || "";
            nomeSalvo = obj.nome || "";
            matriculaSAPSalva = obj.matriculaSAP || "";
        } catch {
            valor = matriculaStorage;
        }
        if (!valor) return;

        setMatricula(valor);
        setNomeUsuario(nomeSalvo); // mostra o nome salvo enquanto re-valida
        setMatriculaSAP(matriculaSAPSalva);

        (async () => {
            const resultado = await validarMatriculaSAP(valor);
            if (resultado === null) {
                localStorage.removeItem("listaMontagem_matriculas_SAP");
                setMatricula("");
                setNomeUsuario("");
                setMatriculaSAP("");
                toast.error("Matrícula não encontrada no SAP");
            } else {
                setNomeUsuario(resultado.nome);
                setMatriculaSAP(resultado.matriculaSAP);
                localStorage.setItem(
                    "listaMontagem_matriculas_SAP",
                    JSON.stringify({ principal: valor, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
                );
            }
        })();
    }, [show, validarMatriculaSAP]);

    // Valida automaticamente (debounce) assim que a matrícula chega a 6 dígitos
    useEffect(() => {
        if (!/^\d{6}$/.test(matricula) || matriculaSAP) return;
        const timeout = setTimeout(async () => {
            setValidandoMatricula(true);
            const resultado = await validarMatriculaSAP(matricula);
            setValidandoMatricula(false);
            if (resultado === null) {
                setNomeUsuario("");
                setMatriculaSAP("");
                toast.error("Matrícula não encontrada no SAP");
                return;
            }
            setNomeUsuario(resultado.nome);
            setMatriculaSAP(resultado.matriculaSAP);
            localStorage.setItem(
                "listaMontagem_matriculas_SAP",
                JSON.stringify({ principal: matricula, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
            );
            toast.success(`Matrícula registrada: ${resultado.nome}`);
        }, 600);
        return () => clearTimeout(timeout);
    }, [matricula, matriculaSAP, validarMatriculaSAP]);

    // Fluxo do leitor de código de barras: assim que a ordem bipada estiver completa
    // (o leitor digita muito rápido; a pausa indica o fim da leitura), busca a peça e
    // já realiza o apontamento automaticamente, sem precisar de Enter nem clique.
    useEffect(() => {
        if (!show || peca.ordem.trim().length < 8 || apontandoRef.current) return;
        const ordem = peca.ordem.trim();
        const timeout = setTimeout(() => {
            dispararApontamento(ordem, peca.codigoVeiculo);
        }, 800);
        return () => clearTimeout(timeout);
    }, [peca.ordem, show]);

    const buscarPeca = async (ordem: string) => {
        try {
            const { data } = await blogFetch.get(`ISAGA_ApontamentoPreMontagem/getPeca`, { params: { ordem } });
            if (!data) {
                toast.error("Peça não encontrada.");
                return null;
            }
            const codigoVeiculo = data.ordem_VehCod || "";
            setPeca((prev) => ({
                ...prev,
                codigoVeiculo,
                projeto: data.subProduto_Desc || "",
                tipoPeca: data.ordem_Part_Short || "",
                veiculo: data.ordem_VehDesc || "",
                ultimoApont: data.ordem_UltSetApontado || "",
            }));
            return { codigoVeiculo };
        } catch (error: any) {
            console.error("Erro ao buscar peça:", error);
            toast.error(error.response?.data || "Erro ao buscar peça.");
            resetForm();
            return null;
        }
    };

    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Apontamento Pré-Montagem</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <div className="card p-3 shadow-sm rounded-3 mb-3">
                    <Form onSubmit={(e: any) => e.preventDefault()}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ordem</Form.Label>
                            <Form.Control
                                type="text"
                                value={peca.ordem}
                                ref={inputRef}
                                onChange={(e) => setPeca((p) => ({ ...p, ordem: e.target.value }))}
                                onKeyDown={handleOrdemKeyDown}
                                placeholder="Bipe a ordem e pressione Enter"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Matrícula SAP</Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={matricula}
                                onChange={(e) => handleMatriculaChange(e.target.value)}
                                placeholder="Digite a matrícula SAP (6 dígitos)"
                                isInvalid={matricula.length === 6 && !validandoMatricula && !matriculaSAP}
                                isValid={!!matriculaSAP}
                            />
                            <Form.Control.Feedback type="invalid">
                                Matrícula não encontrada no SAP.
                            </Form.Control.Feedback>
                            {validandoMatricula ? (
                                <small className="text-muted d-block mt-1">Validando matrícula...</small>
                            ) : nomeUsuario ? (
                                <small className="text-success fw-semibold d-block mt-1">
                                    Operador: {nomeUsuario}
                                </small>
                            ) : null}
                        </Form.Group>
                    </Form>

                    <div className="card p-3 shadow-sm rounded-3">
                        <h6 className="fw-bold text-secondary mb-3">Informações da Peça</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Tipo Peça</label>
                                <input value={peca.tipoPeca} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Código Veículo</label>
                                <input value={peca.codigoVeiculo} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Ultimo Apontamento</label>
                                <input value={peca.ultimoApont} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Projeto</label>
                                <input value={peca.projeto} className="form-control" readOnly />
                            </div>
                            <div className="col-12">
                                <label className="form-label small fw-semibold text-muted">Veículo</label>
                                <input value={peca.veiculo} className="form-control" readOnly />
                            </div>
                        </div>
                    </div>

                </div>

            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <button className="btn btn-outline-secondary px-4" onClick={handleCancel}>Cancelar</button>
                <button
                    className="btn btn-success px-5 fw-semibold"
                    style={{ letterSpacing: '0.05em' }}
                    onClick={handleSubmit}
                    disabled={!matriculaSAP || validandoMatricula || !peca.codigoVeiculo.trim() || isSubmitting}
                    title={
                        !matriculaSAP
                            ? "Valide a matrícula SAP para apontar"
                            : !peca.codigoVeiculo.trim()
                                ? "Informe uma ordem válida com os dados da peça para apontar"
                                : undefined
                    }
                >
                    ✓ Apontar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalApontamentoPmont;
